import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, Paper, IconButton, Button, Chip, Tooltip,
  TextField, Collapse, CircularProgress, Checkbox, Popover
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  AccessTime as ClockIcon,
  Event as DeadlineIcon,
  Flag as FlagIcon,
  Close as CloseIcon,
  CheckCircleOutline as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../utils/ongedaan';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { PRIO_COLORS, TYPE_COLORS } from '../../utils/taakKleuren';
import TaakBlok from '../../ui-components/TaakBlok';
import { getMonday } from '../../utils/weekUtils';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import PageHeader from '../../ui-components/PageHeader';
import { AFGEROND_SET } from '../../utils/taakStatus';

// ─── Constants ───────────────────────────────────────────────────────────────

const DAY_NAMES = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
const MONTH_NAMES = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

// Kleuren uit de gedeelde kaart — Prio 2 was hier rood terwijl de rest van
// de planning oranje toonde; nu overal hetzelfde. Zie utils/taakKleuren.js.

// ─── Helpers ─────────────────────────────────────────────────────────────────


function addDays(date, days) {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function isToday(date) {
  const t = new Date();
  return date.getFullYear() === t.getFullYear() &&
    date.getMonth() === t.getMonth() &&
    date.getDate() === t.getDate();
}


// ─── TaskCard (draggable) ────────────────────────────────────────────────────

function TaskCard({ task, onToggleComplete, onUpdate, overlay = false }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, disabled: overlay });

  const style = overlay ? {} : {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  const isComplete = task.isComplete || AFGEROND_SET.has(task.status);

  return (
    <Box ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TaakBlok
        naam={task.name}
        prio={task.status}
        type={task.type}
        uren={task.hours}
        deadline={task.deadline}
        deadlineType={task.deadlineType}
        isKlaar={isComplete}
        onToggleKlaar={() => onToggleComplete(task)}
        sx={{ cursor: overlay ? 'grabbing' : 'grab', touchAction: 'none' }}
      />
    </Box>
  );
}

// ─── Droppable Day Column ────────────────────────────────────────────────────

function DayColumn({ dateStr, date, tasks, deadlines = [], onDeadlineClick, onToggleComplete, onUpdate, onQuickAdd, isOver }) {
  const { setNodeRef } = useDroppable({ id: `day-${dateStr}` });
  const today = isToday(date);
  const totalHours = tasks.reduce((sum, t) => sum + (parseFloat(t.hours) || 0), 0);

  // Group by project
  const projectGroups = useMemo(() => {
    const groups = {};
    const noProject = [];
    tasks.forEach(t => {
      const projectId = t.projectId;
      const projectLabel = t.project || t.projectName;
      if (projectId && projectLabel) {
        if (!groups[projectId]) groups[projectId] = { label: projectLabel, tasks: [] };
        groups[projectId].tasks.push(t);
      } else {
        noProject.push(t);
      }
    });
    return { grouped: Object.values(groups), ungrouped: noProject };
  }, [tasks]);

  return (
    <Paper
      ref={setNodeRef}
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: '1px solid',
        borderColor: isOver ? '#6366f1' : '#e5e7eb',
        bgcolor: isOver ? '#eef2ff' : (today ? '#fafbff' : '#fff'),
        overflow: 'hidden',
        minHeight: 200,
        transition: 'border-color 0.15s, background 0.15s',
      }}
    >
      {/* Column header */}
      <Box sx={{
        px: 1.5, py: 1,
        borderBottom: '1px solid #e5e7eb',
        bgcolor: today ? '#eef2ff' : 'transparent',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
      }}>
        <Box>
          <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {DAY_NAMES[date.getDay()]}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2, color: today ? '#6366f1' : '#1f2937' }}>
            {date.getDate()}
          </Typography>
        </Box>
        <Chip
          label={`${totalHours % 1 === 0 ? Math.round(totalHours) : totalHours.toFixed(1)}h`}
          size="small"
          sx={{
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 700,
            bgcolor: totalHours > 8 ? '#fee2e2' : totalHours > 0 ? '#f0fdf4' : '#f3f4f6',
            color: totalHours > 8 ? '#dc2626' : totalHours > 0 ? '#059669' : '#9ca3af',
          }}
        />
      </Box>

      {/* Task list */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 0.75, minHeight: 60 }}>
        {/* Opleveringen van deze dag — staan in de kolom zelf, zodat ze bij een
            smal venster niet los van hun dag komen te staan. */}
        {deadlines.map((d, i) => (
          <Box
            key={`${d.id}-${i}`}
            onClick={(e) => onDeadlineClick?.(e, d)}
            sx={{
              px: 1, py: 0.5, mb: 0.5, borderRadius: 1, fontSize: '0.7rem', lineHeight: 1.3,
              backgroundColor: d.telaat ? '#fef2f2' : d.type === 'deliverable' ? '#fff7ed' : '#eff6ff',
              border: `1px solid ${d.telaat ? '#fca5a5' : d.type === 'deliverable' ? '#fdba74' : '#93c5fd'}`,
              color: d.telaat ? '#991b1b' : d.type === 'deliverable' ? '#9a3412' : '#1e40af',
              fontWeight: 600, overflowWrap: 'anywhere',
              cursor: 'pointer', '&:hover': { opacity: 0.85 },
            }}
          >
            {d.type === 'deliverable' ? '🎯 ' : '📦 '}{d.name}
            <Typography sx={{ fontSize: '0.6rem', color: '#6b7280', mt: 0.25, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
              {d.projectName?.replace(/^\d+\.\s*/, '')}
            </Typography>
          </Box>
        ))}

        {/* Grouped by project */}
        {projectGroups.grouped.map((group) => (
          <ProjectGroup
            key={group.label}
            label={group.label}
            tasks={group.tasks}
            onToggleComplete={onToggleComplete}
            onUpdate={onUpdate}
          />
        ))}
        {/* Ungrouped tasks */}
        {projectGroups.ungrouped.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            onToggleComplete={onToggleComplete}
            onUpdate={onUpdate}
          />
        ))}

        {tasks.length === 0 && (
          <Typography variant="caption" sx={{ color: '#d1d5db', display: 'block', textAlign: 'center', py: 2, fontSize: '0.7rem' }}>
            Geen taken
          </Typography>
        )}
      </Box>

      {/* Quick add */}
      <QuickAddTask dateStr={dateStr} onAdd={onQuickAdd} />
    </Paper>
  );
}

// ─── Project Group ───────────────────────────────────────────────────────────

function ProjectGroup({ label, tasks, onToggleComplete, onUpdate }) {
  const [collapsed, setCollapsed] = useState(false);
  const hours = tasks.reduce((sum, t) => sum + (parseFloat(t.fields?.UrenWerk) || 0), 0);

  return (
    <Box sx={{ mb: 0.5 }}>
      <Box
        onClick={() => setCollapsed(!collapsed)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          cursor: 'pointer', px: 0.5, py: 0.25,
          borderRadius: 0.5,
          '&:hover': { bgcolor: '#f3f4f6' },
        }}
      >
        <Typography variant="caption" sx={{ color: '#6b7280', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0)', transition: 'transform 0.15s', fontSize: '0.6rem' }}>
          ▼
        </Typography>
        <Typography
          variant="caption"
          title={label}
          sx={{
            fontWeight: 700, color: '#374151', fontSize: '0.7rem', flex: 1, minWidth: 0,
            display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2,
            overflow: 'hidden', overflowWrap: 'anywhere', lineHeight: 1.3,
          }}
        >
          {label}
        </Typography>
        {hours > 0 && (
          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.6rem', fontWeight: 600 }}>
            {hours % 1 === 0 ? Math.round(hours) : hours.toFixed(1)}h
          </Typography>
        )}
      </Box>
      <Collapse in={!collapsed}>
        <Box sx={{ pl: 0.5 }}>
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onToggleComplete={onToggleComplete}
              onUpdate={onUpdate}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Quick Add Task ──────────────────────────────────────────────────────────

function QuickAddTask({ dateStr, onAdd }) {
  const [expanded, setExpanded] = useState(false);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    try {
      await onAdd(dateStr, name.trim());
      setName('');
      setExpanded(false);
    } catch {
      // error handled upstream
    } finally {
      setSubmitting(false);
    }
  };

  if (!expanded) {
    return (
      <Box
        onClick={() => setExpanded(true)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          px: 1.5, py: 0.75,
          borderTop: '1px solid #f3f4f6',
          cursor: 'pointer',
          color: '#9ca3af',
          '&:hover': { bgcolor: '#f9fafb', color: '#6366f1' },
          transition: 'all 0.12s',
        }}
      >
        <AddIcon sx={{ fontSize: 14 }} />
        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>Taak</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 0.75, py: 0.75, borderTop: '1px solid #e5e7eb' }}>
      <TextField
        autoFocus
        fullWidth
        size="small"
        placeholder="Taaknaam..."
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') handleSubmit();
          if (e.key === 'Escape') { setExpanded(false); setName(''); }
        }}
        disabled={submitting}
        InputProps={{
          sx: { fontSize: '0.8rem', py: 0, height: 32 },
          endAdornment: submitting ? <CircularProgress size={14} /> : (
            <IconButton size="small" onClick={() => { setExpanded(false); setName(''); }}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          ),
        }}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
      />
    </Box>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function TaskBoard() {
  const { accounts } = useMsal();
  const userEmail = accounts?.[0]?.username || '';
  const navigate = useNavigate();

  const [dlAnchor, setDlAnchor] = useState(null);
  const [selectedDl, setSelectedDl] = useState(null);
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));
  const [tasks, setTasks] = useState([]);
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [userRecordId, setUserRecordId] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // Week days (Mon-Fri)
  const weekDays = useMemo(() =>
    [0, 1, 2, 3, 4].map(i => {
      const d = addDays(weekStart, i);
      return { date: d, dateStr: formatISO(d) };
    }),
    [weekStart]
  );

  const weekLabel = useMemo(() => {
    const end = addDays(weekStart, 4);
    const s = weekStart;
    if (s.getMonth() === end.getMonth()) {
      return `${s.getDate()} - ${end.getDate()} ${MONTH_NAMES[s.getMonth()]} ${s.getFullYear()}`;
    }
    return `${s.getDate()} ${MONTH_NAMES[s.getMonth()]} - ${end.getDate()} ${MONTH_NAMES[end.getMonth()]} ${end.getFullYear()}`;
  }, [weekStart]);

  // Fetch tasks + deadlines — taken via het GEDEELDE /Api/person-tasks-range
  // (zelfde bron als Planner To-Do en Capaciteit; geen eigen data-eiland meer).
  const fetchTasks = useCallback(async () => {
    if (!userEmail || !userRecordId) return;
    setLoading(true);
    try {
      const startStr = formatISO(weekStart);
      const endStr = formatISO(addDays(weekStart, 6));
      const params = new URLSearchParams({ startDate: startStr, endDate: endStr, includeCompleted: '1' });
      params.append('personIds[]', userRecordId);
      const [tasksRes, deadlinesRes] = await Promise.all([
        authFetch(`${BACKEND_URL}/Api/person-tasks-range?${params.toString()}`),
        authFetch(`${BACKEND_URL}/Api/my-deadlines?email=${encodeURIComponent(userEmail)}&startDate=${startStr}&endDate=${endStr}`).catch(() => null),
      ]);
      if (!tasksRes.ok) throw new Error('Fout bij ophalen taken');
      const data = await tasksRes.json();
      setTasks(data[userRecordId] || []);
      if (deadlinesRes?.ok) {
        const dlData = await deadlinesRes.json();
        setDeadlines(dlData.byDate || []);
      }
    } catch (err) {
      toast.error(err.message);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [userEmail, userRecordId, weekStart]);

  // Fetch user record ID (for creating tasks)
  useEffect(() => {
    if (!userEmail) return;
    authFetch(`${BACKEND_URL}/Api/personen?email=${encodeURIComponent(userEmail)}`)
      .then(r => r.json())
      .then(data => {
        // Het endpoint retourneert {people, records} — het oude `personen`-veld
        // bestaat niet meer, waardoor deze pagina eeuwig bleef laden (1 sep).
        const lijst = data.records || data.personen || [];
        const laag = userEmail.toLowerCase();
        const person = lijst.find(p =>
          String(p.fields?.Email || '').toLowerCase() === laag
          || String(p.fields?.['Email (from Account)']?.[0] || '').toLowerCase() === laag
        ) || (data.people || []).find(p => String(p.email || '').toLowerCase() === laag);
        if (person) {
          setUserRecordId(person.id);
        } else {
          setLoading(false);
          toast.error('Jouw persoonsrecord is niet gevonden — taken kunnen niet geladen worden.');
        }
      })
      .catch(() => setLoading(false));
  }, [userEmail]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => registreerOngedaanSneltoets(), []);

  // Group tasks by day
  const tasksByDay = useMemo(() => {
    const map = {};
    weekDays.forEach(({ dateStr }) => { map[dateStr] = []; });
    tasks.forEach(t => {
      const d = String(t.startDate || '').split('T')[0];
      if (d && map[d]) map[d].push(t);
    });
    return map;
  }, [tasks, weekDays]);

  // Opleveringen per dag — interne Full Frame-projecten en losse taken zonder
  // deliverable laten we buiten beschouwing (die zijn geen oplevering aan een klant).
  const deadlinesByDay = useMemo(() => {
    const vandaag = formatISO(new Date());
    const isIntern = (naam) => naam && naam.replace(/^\d+\.\s*/, '').startsWith('Full Frame');
    const byDay = {};
    weekDays.forEach(({ dateStr }) => { byDay[dateStr] = []; });
    (deadlines || []).forEach(d => {
      if (isIntern(d.projectName)) return;
      if (d.type === 'task' && !d.deliverableId) return;
      const key = d.deadline?.slice(0, 10);
      if (!byDay[key]) return;
      byDay[key].push({
        ...d,
        telaat: key < vandaag && !AFGEROND_SET.has(d.status),
      });
    });
    return byDay;
  }, [deadlines, weekDays]);

  // Active task for drag overlay
  const activeTask = useMemo(() =>
    activeId ? tasks.find(t => t.id === activeId) : null,
    [activeId, tasks]
  );

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleDragStart = (event) => setActiveId(event.active.id);
  const handleDragOver = (event) => {
    const overId = event.over?.id;
    setOverId(typeof overId === 'string' && overId.startsWith('day-') ? overId : null);
  };
  const handleDragCancel = () => { setActiveId(null); setOverId(null); };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverId(null);

    if (!over) return;

    // Determine target day from droppable ID
    let targetDateStr = null;
    if (typeof over.id === 'string' && over.id.startsWith('day-')) {
      targetDateStr = over.id.replace('day-', '');
    } else {
      // Dropped on a task - find which day that task is in
      const overTask = tasks.find(t => t.id === over.id);
      targetDateStr = overTask?.fields?.StartDateUitvoerder?.split('T')[0];
    }

    if (!targetDateStr) return;

    const taskId = active.id;
    const task = tasks.find(t => t.id === taskId);
    const currentDate = task?.fields?.StartDateUitvoerder?.split('T')[0];

    if (currentDate === targetDateStr) return; // Same day, no change
    // Vorige planning bewaren voor "Ongedaan maken" (eind kan afwijken van start).
    const vorige = {
      StartDateUitvoerder: task?.fields?.StartDateUitvoerder || null,
      EndDateUitvoerder: task?.fields?.EndDateUitvoerder || task?.fields?.StartDateUitvoerder || null,
    };

    // Optimistic update
    setTasks(prev => prev.map(t => (
      t.id === taskId ? { ...t, startDate: targetDateStr, endDate: targetDateStr } : t
    )));

    try {
      const res = await authFetch(`${BACKEND_URL}/Api/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          StartDateUitvoerder: targetDateStr,
          EndDateUitvoerder: targetDateStr,
        }),
      });
      if (!res.ok) throw new Error();
      biedOngedaanAan('Taak verplaatst', async () => {
        await authFetch(`${BACKEND_URL}/Api/tasks/${taskId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(vorige),
        });
        await fetchTasks();
      });
    } catch {
      toast.error('Kon taak niet verplaatsen');
      fetchTasks(); // Revert
    }
  };

  const handleToggleComplete = async (task) => {
    const isComplete = task.isComplete || AFGEROND_SET.has(task.status);
    const newPrio = isComplete ? 'Geen prio' : 'Afgerond';

    // Optimistic
    setTasks(prev => prev.map(t => (
      t.id === task.id ? { ...t, status: newPrio, isComplete: !isComplete } : t
    )));

    try {
      await authFetch(`${BACKEND_URL}/Api/tasks/${task.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ PrioNr: newPrio }),
      });
      if (!isComplete) {
        const vorigePrio = task.status && !AFGEROND_SET.has(task.status) ? task.status : 'Geen prio';
        biedOngedaanAan('Taak afgerond', async () => {
          await authFetch(`${BACKEND_URL}/Api/tasks/${task.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ PrioNr: vorigePrio, Afgerond: false }),
          });
          await fetchTasks();
        });
      }
    } catch {
      toast.error('Kon status niet bijwerken');
      fetchTasks();
    }
  };

  const handleQuickAdd = async (dateStr, name) => {
    const body = {
      NaamTaak: name,
      StartDateUitvoerder: dateStr,
      EndDateUitvoerder: dateStr,
      PrioNr: 'Geen prio',
    };
    if (userRecordId) body.Assignee = [userRecordId];

    try {
      const res = await authFetch(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error();
      toast.success('Taak aangemaakt', { duration: 1500, style: { fontSize: '0.8rem' } });
      fetchTasks();
    } catch {
      toast.error('Kon taak niet aanmaken');
      throw new Error();
    }
  };

  const navigateWeek = (dir) => setWeekStart(prev => addDays(prev, dir * 7));
  const goToToday = () => setWeekStart(getMonday(new Date()));

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      {/* Header */}
      <PageHeader
        icon="view_week"
        title="Weekbord"
        subtitle={weekLabel}
        rightActions={(
          <>
            <IconButton size="small" onClick={() => navigateWeek(-1)}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Button
              size="small"
              variant="outlined"
              onClick={goToToday}
              sx={{
                textTransform: 'none', fontSize: '0.75rem', fontWeight: 600,
                borderColor: '#e5e7eb', color: '#374151', minWidth: 0, px: 1.5, py: 0.25,
                '&:hover': { borderColor: '#526075', color: '#526075' },
              }}
            >
              Vandaag
            </Button>
            <IconButton size="small" onClick={() => navigateWeek(1)}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={fetchTasks} disabled={loading} sx={{ ml: 1 }}>
              <RefreshIcon fontSize="small" sx={{ animation: loading ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }} />
            </IconButton>
          </>
        )}
      />

      {/* Deadline popover */}
      <Popover
        open={Boolean(dlAnchor)}
        anchorEl={dlAnchor}
        onClose={() => { setDlAnchor(null); setSelectedDl(null); }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 340, borderRadius: 2 } } }}
      >
        {selectedDl && (
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', mb: 0.5 }}>
              {selectedDl.type === 'deliverable' ? '🎯 ' : '📦 '}{selectedDl.name}
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 0.5 }}>
              {selectedDl.projectNumber ? `${selectedDl.projectNumber}. ` : ''}{selectedDl.projectName?.replace(/^\d+\.\s*/, '')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <Chip label={new Date(selectedDl.deadline).toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' })} size="small" sx={{ fontSize: '0.7rem' }} />
              {selectedDl.deadlineType && <Chip label={selectedDl.deadlineType} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
            </Box>
            {selectedDl.type === 'task' && (
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: '0.7rem', color: '#9ca3af', mb: 0.5 }}>Status</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {['Prio 1', 'Prio 2', 'Prio 3', 'Geen prio', 'Wacht op feedback', 'Afgerond', 'Opgestuurd'].map(s => (
                    <Chip
                      key={s}
                      label={s}
                      size="small"
                      variant={selectedDl.status === s ? 'filled' : 'outlined'}
                      sx={{
                        fontSize: '0.65rem', height: 22, cursor: 'pointer',
                        ...(selectedDl.status === s ? { bgcolor: PRIO_COLORS[s] || '#6366f1', color: '#fff', borderColor: PRIO_COLORS[s] } : { borderColor: '#e5e7eb' }),
                      }}
                      onClick={async () => {
                        try {
                          await authFetch(`${BACKEND_URL}/Api/tasks/${selectedDl.id}`, {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ fields: { PrioNr: s } }),
                          });
                          setSelectedDl({ ...selectedDl, status: s });
                          toast.success(`Status → ${s}`);
                        } catch { toast.error('Status wijzigen mislukt'); }
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
            <Button
              size="small"
              variant="contained"
              fullWidth
              onClick={() => { navigate(`/projects/${selectedDl.projectId}`); setDlAnchor(null); }}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              Ga naar project →
            </Button>
          </Box>
        )}
      </Popover>

      {/* Week grid */}
      {loading && tasks.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          {/* Vijf vaste kolommen worden onleesbaar zodra het venster niet full screen
              is; daarom minder kolommen naarmate het smaller wordt. */}
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(5, 1fr)',
            },
            gap: 1.5,
            alignItems: 'start',
          }}>
            {weekDays.map(({ date, dateStr }) => (
              <DayColumn
                key={dateStr}
                dateStr={dateStr}
                date={date}
                tasks={tasksByDay[dateStr] || []}
                deadlines={deadlinesByDay[dateStr] || []}
                onDeadlineClick={(e, d) => { setDlAnchor(e.currentTarget); setSelectedDl(d); }}
                onToggleComplete={handleToggleComplete}
                onUpdate={() => {}}
                onQuickAdd={handleQuickAdd}
                isOver={overId === `day-${dateStr}`}
              />
            ))}
          </Box>

          {/* Drag overlay */}
          <DragOverlay>
            {activeTask ? (
              <Box sx={{ width: 220, opacity: 0.9 }}>
                <TaskCard task={activeTask} onToggleComplete={() => {}} onUpdate={() => {}} overlay />
              </Box>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {/* Legend */}
      <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
        {Object.entries(TYPE_COLORS).map(([type, color]) => (
          <Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 3, borderRadius: 1, bgcolor: color }} />
            <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.65rem' }}>{type}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
